import React, { ChangeEvent, useState, useEffect } from 'react';
import UseApi from 'hooks/useApi';

interface IImageUploadProps {
  value: string;
  label: string;
  id: string;
  name: string;
  onChange: (e: any) => void;
  error?: string | null;
  disabled?: boolean;
  variant?: string;
  extra?: string;
  folder?: string;
}

const ImageUpload: React.FC<IImageUploadProps> = ({
  value,
  label,
  id,
  name,
  onChange,
  error,
  disabled = false,
  variant = 'auth',
  extra = '',
  folder = 'packages',
}) => {
  const { postApi } = UseApi();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filename, setFilename] = useState<string>('');
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    // Add proper type checking before using string methods
    if (value && typeof value === 'string' && value.includes('/')) {
      try {
        const parts = value.split('/');
        if (parts.length > 0) {
          // Extract filename from URL
          const filenameWithExt = parts[parts.length - 1];
          setFilename(filenameWithExt);
        }
      } catch (err) {
        console.error('Error parsing filename:', err);
      }
    }
  }, [value]);

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setUploadError(null);

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;

        const uploadData = {
          fileName: file.name,
          fileBase64: base64String,
          folder: folder,
        };

        try {
          const response = await postApi('image/upload', uploadData);

          if (response?.data) {
            // Create a custom event object to match the expected format
            onChange({
              target: {
                name: name,
                value: response.data,
              },
            });
            setFilename(file.name);
          } else {
            setUploadError('Failed to upload image');
          }
        } catch (err) {
          setUploadError('Error uploading image');
          console.error('Upload error:', err);
        } finally {
          setIsLoading(false);
        }
      };

      reader.onerror = () => {
        setUploadError('Error reading file');
        setIsLoading(false);
      };

      reader.readAsDataURL(file);
    } catch (err) {
      setUploadError('Error processing image');
      setIsLoading(false);
    }
  };

  return (
    <div className={`${extra}`}>
      <label
        htmlFor={id}
        className={`text-sm font-medium text-gray-900 ${
          error ? 'text-red-500' : ''
        }`}
      >
        {label}
      </label>

      <div
        className={`mt-2 flex h-12 w-full items-center justify-between rounded-xl border 
        ${error || uploadError ? 'border-red-500' : 'border-gray-300'} 
        bg-white px-3 outline-none
        ${
          disabled || isLoading
            ? 'cursor-not-allowed opacity-70'
            : 'cursor-pointer'
        }
        ${variant === 'auth' ? 'border-gray-300' : ''}`}
      >
        <div className="flex-grow overflow-hidden text-ellipsis whitespace-nowrap">
          {isLoading ? (
            <div className="flex items-center">
              <svg
                className="-ml-1 mr-3 h-5 w-5 animate-spin text-gray-700"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="text-sm text-gray-500">Uploading...</span>
            </div>
          ) : (
            <span className="text-sm text-gray-700">
              {filename || 'Choose an image file'}
            </span>
          )}
        </div>

        <label htmlFor={id} className="cursor-pointer">
          <span className="rounded bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200">
            Browse
          </span>
          <input
            type="file"
            id={id}
            accept="image/*"
            className="hidden"
            disabled={disabled || isLoading}
            onChange={handleImageChange}
          />
        </label>
      </div>

      {(error || uploadError) && (
        <p className="mt-1 text-xs text-red-500">{error || uploadError}</p>
      )}
    </div>
  );
};

export default ImageUpload;
